import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';

export default function TeamPage() {
  const toast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Migration 072. Blank means "let the server use its default", which is `viewer` -- an
  // account someone forgot to scope should be able to do nothing, not everything.
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [roles, setRoles] = useState([]);
  const [listError, setListError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listOperatorAccounts();
      setAccounts(data.accounts);
      setRoles(data.roles);
      setListError(null);
    } catch (err) {
      // A non-admin operator legitimately gets a 403 here. That is not an error state to shout
      // about -- it is the role model working -- so it renders as an explanation, not a failure.
      setListError(err instanceof ApiError ? err.message : 'Could not load operator accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function changeRole(account, nextRole) {
    if (nextRole === account.role) return;
    if (!window.confirm(`Change ${account.email} from ${account.role} to ${nextRole}?`)) return;
    setChanging(account.id);
    try {
      await api.setOperatorRole(account.id, nextRole);
      toast.success(`${account.email} is now ${nextRole}.`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not change the role.');
    } finally {
      setChanging(null);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    setSuccess(null);
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.');
      return;
    }
    setSaving(true);
    try {
      await api.upsertOperatorAccount({ name: name.trim(), email: email.trim(), password, ...(role ? { role } : {}) });
      setSuccess(`Account saved for ${email.trim()}.`);
      toast.success('Operator account saved.');
      setPassword('');
      load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not save operator account.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <PageHeader title="Team" subtitle="Create or reset operator accounts for BidDigi's admin console." />

      <section className="card card--flush">
        <div className="card__header card__header--padded">
          <h2 className="card__title">Who has access</h2>
          <span className="card__hint">
            Changing a role here does not reset a password or sign anyone out.
          </span>
        </div>
        {loading && <LoadingState label="Loading accounts…" />}
        {!loading && listError && (
          <EmptyState title="Operator accounts are admin-only" message={listError} />
        )}
        {!loading && !listError && accounts && accounts.length === 0 && (
          <EmptyState title="No operator accounts yet" message="Create the first one below." />
        )}
        {!loading && !listError && accounts && accounts.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id}>
                    <td className="cell-title">{a.name}</td>
                    <td className="cell-muted">{a.email}</td>
                    <td>
                      <select
                        value={a.role}
                        disabled={changing === a.id}
                        onChange={(e) => changeRole(a, e.target.value)}
                      >
                        {roles.map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
                      </select>
                    </td>
                    <td><span className={`status-badge status-badge--${a.status === 'active' ? 'active' : 'paused'}`}>{a.status}</span></td>
                    <td className="cell-muted" style={{ maxWidth: '24rem' }}>
                      {(roles.find((r) => r.name === a.role) || {}).description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card" style={{ maxWidth: 480 }}>
        <div className="card__header">
          <h2 className="card__title">Operator account</h2>
        </div>
        <form onSubmit={handleSubmit} className="form">
          <label className="field">
            <span className="field__label">Name</span>
            <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Tanmay Shah" />
          </label>
          <label className="field">
            <span className="field__label">Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tanmay@biddigi.com" />
          </label>
          <label className="field">
            <span className="field__label">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
            />
          </label>
          <label className="field">
            <span className="field__label">Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="">Viewer (default — read only)</option>
              {roles.filter((r) => r.name !== 'viewer').map((r) => (
                <option key={r.name} value={r.name}>{r.label}</option>
              ))}
            </select>
          </label>
          {role && roles.find((r) => r.name === role) && (
            <p className="card__hint">{roles.find((r) => r.name === role).description}</p>
          )}
          <p className="card__hint">
            Submitting an existing email resets that account's password. Leaving Role at its
            current value does <strong>not</strong> re-scope the account — only an explicit
            change does.
          </p>
          {formError && <div className="form-error">{formError}</div>}
          {success && <div className="form-success">{success}</div>}
          <div className="form__actions">
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save account'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
